/**
 * Marker shape
 *
 * The highlight band is a chisel-tip stroke: a rectangle with a slanted cut at
 * each end, like the flat nib of a highlighter. It is built from three
 * background layers (left wedge, body, right wedge) so it still wraps across
 * lines and animates with plain `background-size` / `background-position`
 * transitions. With slice fragmentation the layers lay out along the phrase's
 * fragments end to end, so the wedges land on the true start and end.
 *
 * Every phrase gets its own stroke: a seed derived from its text picks the
 * slant widths, which way each end leans (mostly "/", sometimes "\"), and a
 * small jitter in height and vertical position. The same phrase always draws
 * the same stroke.
 *
 * Vertical placement is in em from the top of the span's padding box rather
 * than a percentage of it. A percentage pins the band to the bottom of the
 * glyph box, which for fonts with a deep descent (DM Sans) leaves it hanging
 * under the text; em offsets keep it on the x-height zone, baseline-relative.
 */

/** Small fast seeded PRNG (mulberry32). */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** FNV-1a string hash, for seeding from a phrase. */
export function hashSeed(text: string): number {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export interface MarkerShape {
  /** Width of the slanted cut at the start / end, em. */
  left: number;
  right: number;
  /** "/" leans up-right, "\" leans up-left. */
  leftLean: "/" | "\\";
  rightLean: "/" | "\\";
  /** Band height and distance of its top edge below the padding-box top, em. */
  height: number;
  top: number;
}

export function markerShape(seed: number, full = false): MarkerShape {
  const rand = mulberry32(seed);
  const range = (min: number, max: number) => min + rand() * (max - min);
  const round = (n: number) => Math.round(n * 1000) / 1000;
  return {
    left: round(range(0.12, 0.3)),
    right: round(range(0.12, 0.3)),
    leftLean: rand() < 0.8 ? "/" : "\\",
    rightLean: rand() < 0.8 ? "/" : "\\",
    // `full` covers the ascender zone too, for dark-ink highlights.
    height: round(full ? range(0.98, 1.06) : range(0.68, 0.78)),
    top: round(full ? range(0.16, 0.22) : range(0.4, 0.48)),
  };
}

const em = (n: number) => `${n}em`;

export interface MarkerBackground {
  backgroundImage: string;
  backgroundSize: string;
  backgroundPosition: string;
  /** Only with `ink`: which layers are clipped to the glyphs. */
  backgroundClip?: string;
}

/** How far the ink reveal trails the band's leading edge, em. */
const INK_LAG = 0.35;

/**
 * Background layers for a stroke in `band`. `drawn` false is the collapsed
 * start of the sweep; true is the finished stroke. The body and the right
 * wedge are the parts that travel, and both endpoints are calc() mixes of the
 * same terms, so a transition between them stays in step.
 */
export function markerBackground(
  band: string,
  shape: MarkerShape,
  drawn: boolean,
  ink?: string,
): MarkerBackground {
  const { left, right, leftLean, rightLean, height, top } = shape;
  const solid = `linear-gradient(${band}, ${band})`;
  // Hard diagonal through the wedge box's corners. The filled half must be the
  // one that includes the whole edge facing the body, or it floats free as a
  // detached triangle: a "/" left end fills the lower-right half, "\\" the
  // upper-right; a "/" right end fills the upper-left, "\\" the lower-left.
  const leftWedge = `linear-gradient(to ${leftLean === "/" ? "top left" : "bottom left"}, ${band} 50%, transparent 50%)`;
  const rightWedge = `linear-gradient(to ${rightLean === "/" ? "bottom right" : "top right"}, ${band} 50%, transparent 50%)`;
  const h = em(height);
  const y = em(top);
  const bodyWidth = drawn
    ? `calc(100% - ${em(left)} - ${em(right)})`
    : "calc(0% - 0em - 0em)";
  const bg: MarkerBackground = {
    backgroundImage: [leftWedge, solid, rightWedge].join(", "),
    backgroundSize: [
      `${drawn ? em(left) : "0em"} ${h}`,
      `${bodyWidth} ${h}`,
      `${drawn ? em(right) : "0em"} ${h}`,
    ].join(", "),
    backgroundPosition: [
      `0% ${y}`,
      `${em(left)} ${y}`,
      `${drawn ? "100%" : "0%"} ${y}`,
    ].join(", "),
  };
  if (!ink) return bg;

  // Two more layers, clipped to the glyphs (the text itself is made
  // transparent by the caller): the ink, revealed along the sweep just behind
  // the band, over the text's own colour. Letters stay readable until the band
  // reaches them, then flip to dark as it passes: a clipping reveal.
  // First-listed layers paint on top, so the glyph layers go first.
  return {
    backgroundImage: [
      `linear-gradient(${ink}, ${ink})`,
      "linear-gradient(currentColor, currentColor)",
      bg.backgroundImage,
    ].join(", "),
    backgroundSize: [
      drawn ? `calc(100% - ${em(INK_LAG)}) 100%` : "calc(0% - 0em) 100%",
      "100% 100%",
      bg.backgroundSize,
    ].join(", "),
    backgroundPosition: ["0 0", "0 0", bg.backgroundPosition].join(", "),
    backgroundClip: "text, text, border-box, border-box, border-box",
  };
}
